import { Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateLineChartStyleOptions,
  updateAreaChartStyleOptions,
  updateStackedAreaChartStyleOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { ColorSchemes } from './ColorScheme';
import { RootState } from '../../../redux';
import CustomColorBuilder from './CustomColorBuilder';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';
import { limitChartData } from '../../CommonFunctions/CommonFunctions';

const SeriesColorControls = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });
  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const chartType = chartProperties.properties[propKey].chartType;

  let currentControls: any = {};
  let updateAction: Function | null = null;

  if (chartType === 'line') {
    currentControls = chartControls.properties[propKey].lineChartControls || {};
    updateAction = updateLineChartStyleOptions;
  } else if (chartType === 'area') {
    currentControls = chartControls.properties[propKey].areaChartControls || {};
    updateAction = updateAreaChartStyleOptions;
  } else if (chartType === 'stackedArea') {
    currentControls = chartControls.properties[propKey].stackedAreaChartControls || {};
    updateAction = updateStackedAreaChartStyleOptions;
  }

  const updateStyleOptions = (propKey: string, option: string, value: any) => {
    if (updateAction) {
      dispatch(updateAction(propKey, option, value));
    }
  };

  const chartData = chartControls.properties[propKey].chartData || [];

  const allThemes = useCombinedColorSchemes();
  const currentSchemeName = chartControls.properties[propKey].colorScheme;

  const selectedScheme = allThemes.find((scheme) => scheme.name === currentSchemeName);
  const themeColors = selectedScheme?.colors || ColorSchemes[0].colors;

  const lastManualSchemeRef = useRef(currentSchemeName);

  useEffect(() => {
    if (currentControls.manualColor) {
      lastManualSchemeRef.current = currentSchemeName;
    }
  }, [currentControls.manualColor, currentSchemeName]);

  const chartAxes = chartProperties.properties[propKey].chartAxes;
  const dimensionFields = chartAxes[1]?.fields || [];
  const measureFields = chartAxes[3]?.fields || [];
  const hasSplitBy = chartAxes[2]?.fields?.length > 0;

  const sortingMeasureName = measureFields[0]?.displayname || '';
  const limitedChartData = limitChartData(chartData, 200, sortingMeasureName);

  let itemsToColor: string[] = [];
  let categoryLabel = '';
  let coloringFieldId = '';

  if (hasSplitBy) {
    const splitField = chartAxes[2].fields[0];
    const splitFieldName = splitField.displayname || splitField.fieldname;
    categoryLabel = splitField.displayname || splitField.fieldname;
    coloringFieldId = splitField.displayname || splitField.fieldname;

    const rawValues = limitedChartData.map((d: any) => {
      const val = d[splitFieldName];
      return val === null || val === 'null' ? '(Blank)' : String(val);
    });

    itemsToColor = Array.from(new Set(rawValues));
  } else {
    categoryLabel = 'Measures';
    coloringFieldId = '__MEASURES_CONST_ID__';
    itemsToColor = measureFields.map((f: any) => f.displayname);
  }

  const activeItemsSignature = itemsToColor.join('||');
  const prevFieldIdRef = useRef(coloringFieldId);
  const prevSignatureRef = useRef(activeItemsSignature);

  useEffect(() => {
    const hasIdChanged = prevFieldIdRef.current && prevFieldIdRef.current !== coloringFieldId;

    const hasValuesChangedInMeasureMode =
      !hasSplitBy && prevSignatureRef.current !== activeItemsSignature;

    if (hasIdChanged || hasValuesChangedInMeasureMode) {
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    } else if (!hasSplitBy) {
      const currentMap = currentControls.nodeValuesColors || {};
      const currentSavedKeys = Object.keys(currentMap);
      const keysToRemove = currentSavedKeys.filter((key) => !itemsToColor.includes(key));

      if (keysToRemove.length > 0) {
        const newMap = { ...currentMap };
        keysToRemove.forEach((key) => delete newMap[key]);
        updateStyleOptions(propKey, 'nodeValuesColors', newMap);
      }
    }

    prevFieldIdRef.current = coloringFieldId;
    prevSignatureRef.current = activeItemsSignature;
  }, [coloringFieldId, activeItemsSignature, hasSplitBy, propKey, dispatch]);

  const manualKeys = Object.keys(currentControls.nodeValuesColors || {});

  const displayItems = itemsToColor.filter((item) => item !== '(Blank)');

  const customList = manualKeys.filter(
    (key) => !itemsToColor.includes(key) && key !== '(Blank)' && key !== 'null',
  );

  const globalAllColor = currentControls.allSeriesColor || '';

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const hasIndividualChanges = Object.keys(currentControls.nodeValuesColors || {}).length > 0;
  const hasGlobalChange = !!globalAllColor;
  const showReset = hasGlobalChange || hasIndividualChanges;

  const handleColorChange = (color: string) => {
    if (selectedSeriesName === 'ALL_SERIES_COLOR') {
      updateStyleOptions(propKey, 'allSeriesColor', color);
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    } else {
      const currentMap = currentControls.nodeValuesColors || {};
      const newMap = { ...currentMap, [selectedSeriesName]: color };
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  };

  const handleAddCustomColor = (name: string, color: string) => {
    const currentMap = currentControls.nodeValuesColors || {};
    const newMap = { ...currentMap, [name]: color };
    updateStyleOptions(propKey, 'nodeValuesColors', newMap);
  };

  const handleRemoveColorRule = (name: string) => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    delete currentMap[name];
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleRemoveAllCustomColors = () => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    customList.forEach((key) => {
      delete currentMap[key];
    });
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleFullReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateStyleOptions(propKey, 'allSeriesColor', '');
    updateStyleOptions(propKey, 'nodeValuesColors', {});
  };

  const handleOpenPicker = (e: React.MouseEvent<HTMLElement>, name: string, color: string) => {
    setSelectedSeriesName(name);
    setSelectedColor(color);
    setAnchorEl(e.currentTarget);
  };

  const renderAllControl = () => {
    const displayColor = globalAllColor || '#ffffff';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          marginBottom: 12,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
            backgroundImage: !globalAllColor
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
              : 'none',
            backgroundSize: !globalAllColor ? '10px 10px' : 'auto',
            backgroundPosition: !globalAllColor ? '0 0, 0 5px, 5px -5px, -5px 0px' : '0 0',
          }}
          onClick={(e) => handleOpenPicker(e, 'ALL_SERIES_COLOR', globalAllColor || '#000000')}
        />

        <label
          style={{
            fontSize: '13px',
            fontWeight: 600,
            marginLeft: '5px',
            flexGrow: 1,
            cursor: 'default',
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          All
        </label>

        {showReset && (
          <IconButton
            size="small"
            onClick={handleFullReset}
            sx={{
              padding: '2px',
              marginLeft: '5px',
              color: iconColor || 'lightgray',
              '&:hover': {
                color: iconColor || 'gray',
                backgroundColor: 'transparent',
              },
            }}
            title="Reset All Manual Changes"
          >
            <RestartAltIcon style={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderColorRow = (itemName: string, isCustomList: boolean) => {
    const manualMap = currentControls.nodeValuesColors || {};

    const dataIndex = itemsToColor.indexOf(itemName);

    let defaultBaseColor = '#cccccc';
    if (dataIndex >= 0) {
      if (globalAllColor) {
        defaultBaseColor = globalAllColor;
      } else {
        defaultBaseColor = themeColors[dataIndex % themeColors.length];
      }
    }

    const displayColor = manualMap[itemName] || defaultBaseColor;
    const isFutureValue = isCustomList;
    const labelText = itemName === 'null' ? '(Blank)' : itemName;

    return (
      <div
        key={itemName}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
          }}
          onClick={(e) => handleOpenPicker(e, itemName, displayColor)}
        />
        <label
          style={{
            fontSize: '12px',
            marginLeft: '5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isFutureValue ? '#2bb9bb' : iconColor || 'inherit',
            cursor: 'default',
            flexGrow: 1,
            textAlign: 'left',
          }}
          title={isFutureValue ? `${labelText} (Custom Value)` : labelText}
        >
          {labelText}
        </label>
      </div>
    );
  };

  const renderColorControls = () => {
    return (
      <div
        style={{ marginBottom: '10px', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px' }}
      >
        <div
          className="optionDescription"
          style={{ marginTop: '0', display: 'flex', justifyContent: 'space-around' }}
        >
          <label
            style={{
              width: '100%',
              fontWeight: 600,
              fontSize: '13px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            {categoryLabel}
          </label>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ padding: 2 }}
            title={isExpanded ? 'Collapse List' : 'Expand List'}
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </div>

        <Collapse in={isExpanded}>
          <div
            style={{
              marginTop: '5px',
              paddingLeft: '10px',
              paddingBottom: '10px',
            }}
          >
            {renderAllControl()}
            {displayItems.map((itemName) => renderColorRow(itemName, false))}
          </div>
        </Collapse>

        <div
          style={{
            marginTop: '15px',
            padding: '10px 10px 0px',
            borderTop: '1px dashed #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="caption" style={{ color: iconColor || '#888', fontWeight: 600 }}>
            Default for Blank / Empty
          </Typography>

          {renderColorRow('(Blank)', false)}
        </div>

        <CustomColorBuilder
          onAdd={handleAddCustomColor}
          existingColors={currentControls.nodeValuesColors || {}}
          onRemove={handleRemoveColorRule}
          onRemoveAll={handleRemoveAllCustomColors}
          customList={customList}
          colorMap={currentControls.nodeValuesColors || {}}
          colorPalette={selectedScheme?.colors || ColorSchemes[0].colors}
          onColorChange={handleAddCustomColor}
          iconColor={iconColor}
        />
      </div>
    );
  };

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={currentControls.manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          const isSwitchingToManual = key === 'manual';

          if (isSwitchingToManual) {
            //   if scheme changed since we were last in manual
            if (currentSchemeName !== lastManualSchemeRef.current) {
              // Scheme changed! Wipe old values
              updateStyleOptions(propKey, 'allSeriesColor', '');
              updateStyleOptions(propKey, 'nodeValuesColors', {});
            }
            updateStyleOptions(propKey, 'manualColor', true);
          } else {
            //    not wiping values here. Just switching mode.
            updateStyleOptions(propKey, 'manualColor', false);
          }
        }}
        style={{ width: '94%' }}
      />

      <div>
        {currentControls.manualColor ? (
          <>
            <div style={{ marginBottom: '10px', paddingRight: '10px' }}>
              {renderColorControls()}
            </div>

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu=""
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesColorControls;
